import type { PermissionService } from '@vedalib/crud'
import { PermissionsKey } from '@vedalib/crud'
import type { SetState } from 'ahooks/lib/createUseStorageState'
import { gqlClient } from 'gql'
import lodash from 'lodash'
import * as R from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createContext } from 'use-context-selector'

import { ENUM_VALUE_MAP } from 'components/uiKit/SortPopover/constants'
import type { SortUiEnum } from 'components/uiKit/SortPopover/types'
import { EMPTY_ARRAY, EMPTY_OBJECT, NOOP } from 'constants/commonConstans'
import type {
  EditorTemplateAllTab,
  TemplatesListSchemaFragment,
  EditorTemplateOrder,
  TemplateTag,
  EmployeePreferencesTemplates,
  TemplatesAllQueryVariables,
} from 'gql/__generated__/graphql'
import { TemplateCollectionCreatedFor, EmployeePreferencesTab } from 'gql/__generated__/graphql'
import { useTemplatesAll, useTemplateTagsByCollection } from 'gql/templates/apollo'
import { templatesGetAllIdsQuery } from 'gql/templates/gql/mutations'
import { TemplateTagEnum } from 'routes/routes/App/routes/Company/routes/Templates/components/TemplateTags/types'
import { setEmployeeToProjectPreferences } from 'services/Store/Project/actions'
import { BlockMode, SectionTypeEnum } from 'services/Store/Project/enums'
import { useProjectDispatch } from 'services/Store/Project/hooks'
import { setUserPreferencesItems } from 'services/Store/Users/reducer'
import { selectUserPreferences } from 'services/Store/Users/selectors'
import { useAppDispatch, useAppSelector } from 'services/Store/hooks'
import { t } from 'services/Translation'
import { getEnumLabel } from 'utils/enum'
import { isCtrl } from 'utils/events'

import type { ICollectionType } from './TemplatePickerModal'
import { COUNT_LIMIT } from './helper'
import { TemplatePickerMode } from './types'

export type ITemplateTag = Omit<TemplateTag, 'id'> & { id: null | string }

interface IUseTags {
  employeePreferencesTag?: Omit<ITemplateTag, '__typename'>
  collectionId?: string
  companyId: string
  projectId?: string
  userPreferencesKey?: string
  filterEmptyTags?: boolean
  mode?: BlockMode[]
  updatePreferences?: (params: {
    paramsTab?: EmployeePreferencesTab
    paramsCollection?: ICollectionType
    paramsTag?: ITemplateTag
  }) => void
}

export const STEP_BLOCK_MODE_MAP = {
  [0]: [BlockMode.start],
  [1]: [BlockMode.questions],
  [2]: [BlockMode.end],
  [Infinity]: [BlockMode.questions],
}

export const MODE_BY_SECTION_TYPE: Record<SectionTypeEnum, BlockMode[]> = {
  [SectionTypeEnum.chapter]: [],
  [SectionTypeEnum.cover]: [BlockMode.cover],
  [SectionTypeEnum.landing]: [BlockMode.view, BlockMode.questions],
  [SectionTypeEnum.test]: [BlockMode.start, BlockMode.questions, BlockMode.end],
}

export const getTemplateTagOptions = (templateTags: TemplateTag[]) =>
  templateTags.map(({ name, id }) => ({
    label: name || t('input.placeholder.templateTag'),
    value: id,
  }))

export const getModeBySectionType = (sectionType: SectionTypeEnum = SectionTypeEnum.chapter) =>
  MODE_BY_SECTION_TYPE[sectionType]

export const getModeBySectionTypeOnCreate = (
  sectionType: SectionTypeEnum = SectionTypeEnum.chapter,
  hasStart: boolean = true,
) =>
  sectionType === SectionTypeEnum.test
    ? hasStart
      ? [BlockMode.questions]
      : [BlockMode.start]
    : getModeBySectionType(sectionType)

export const LABEL_ALL_TAG = getEnumLabel('TemplateTagEnum', TemplateTagEnum.ALL)
export const DEF_TAG = { name: LABEL_ALL_TAG, id: null, position: 0 }
export const DEF_OPTION_TAG = { value: DEF_TAG.id, label: DEF_TAG.name }

export const useTags = ({
  employeePreferencesTag,
  companyId,
  collectionId = '',
  projectId,
  updatePreferences,
  userPreferencesKey = '',
  filterEmptyTags = true,
  mode,
}: IUseTags) => {
  const dispatch = useProjectDispatch()

  const tagsCollection =
    (useAppSelector(selectUserPreferences)[userPreferencesKey] as ITemplateTag) || DEF_TAG

  const setTagsCollection = useCallback(
    (tag: ITemplateTag) => dispatch(setUserPreferencesItems({ [userPreferencesKey]: tag })),
    [dispatch, userPreferencesKey],
  )
  const { data } = useTemplateTagsByCollection({
    variables: { companyId, collectionId, filter: { filterEmptyTags, mode } },
    skip: !collectionId,
    fetchPolicy: 'cache-and-network',
  })
  const tags = useMemo(() => data?.data.templateTags || [], [data?.data.templateTags])

  const getExistingTag = useCallback(
    (tagId?: string | null, tagName?: string | null) =>
      tags.find(({ id, name }) => id === tagId || name === tagName) || DEF_TAG,
    [tags],
  )

  const setTag = useCallback(
    (tagId: string | null) => {
      const tag = getExistingTag(tagId)
      if (projectId && updatePreferences) {
        updatePreferences({ paramsTag: tag })
      } else {
        setTagsCollection(tag)
      }
    },
    [projectId, setTagsCollection, updatePreferences, getExistingTag],
  )

  const tag = useMemo(
    () => getExistingTag(projectId ? employeePreferencesTag?.id : tagsCollection?.id),
    [employeePreferencesTag, projectId, getExistingTag, tagsCollection],
  )

  // Set value on first render - last using tag by name or id
  useEffect(() => {
    const baseTag = projectId ? employeePreferencesTag : tagsCollection
    const foundedTagId = getExistingTag(baseTag?.id, baseTag?.name)?.id
    if (foundedTagId !== null) {
      setTag(foundedTagId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags.length])

  return { tag, setTag, tags }
}

export const useSelected = () => {
  const [selected, setSelected] = useState<ITemplatePickerContext['selected']>({
    multi: [],
    [BlockMode.cover]: null,
    [BlockMode.start]: null,
    [BlockMode.end]: null,
  })

  const removeByIndex = useCallback((value: number, e: React.MouseEvent) => {
    if (!isCtrl(e)) {
      setSelected((prev) => ({ ...prev, multi: prev.multi.filter((_str, i) => i !== value - 1) }))
    }
  }, [])

  const addItem = useCallback(
    (item: TemplatesListSchemaFragment, e: React.MouseEvent) => {
      const lastIndex = selected.multi.map((uuid) => uuid === item.id).lastIndexOf(true)
      if (lastIndex >= 0 && !isCtrl(e)) {
        removeByIndex(lastIndex + 1, e)
        return
      }
      if ((lastIndex < 0 && !isCtrl(e)) || isCtrl(e) || !selected.multi.length) {
        setSelected((prev) => {
          return { ...prev, multi: [...prev.multi, item.id] }
        })
      }
    },
    [selected.multi, removeByIndex],
  )

  const clearSelect = useCallback(() => {
    setSelected((prev) => ({ ...prev, multi: [] }))
  }, [])

  return { selected, setSelected, addItem, removeByIndex, clearSelect }
}

export type HookParams = {
  sectionType?: SectionTypeEnum
  tag?: string | null
  search?: string
  editorTemplateGroupId?: string
  companyId: string
  projectId?: string
  tab?: EmployeePreferencesTab
  sectionId?: string
  order?: EditorTemplateOrder | null
  limit?: number
  offset?: number
  blockMode?: BlockMode[]
}

const paramsToVariables = ({
  tag,
  search,
  editorTemplateGroupId,
  companyId,
  projectId,
  tab = EmployeePreferencesTab.collections,
  order,
  limit = COUNT_LIMIT,
  offset = 0,
  blockMode,
}: HookParams): TemplatesAllQueryVariables => ({
  companyId,
  data: {
    projectId,
    mode: blockMode,
    editorTemplateGroupId:
      tab === EmployeePreferencesTab.collections ? editorTemplateGroupId : undefined,
    query: search,
    tab: tab as unknown as EditorTemplateAllTab,
    // type: undefined, // not used on backend - drop from shema
    templateTag: tag || null,
    order,
    pagination: { limit, offset },
  },
})

export const useLoadAllIds = (params: HookParams) => {
  return async () =>
    gqlClient.core.query({
      query: templatesGetAllIdsQuery,
      variables: lodash.omit(paramsToVariables(params), 'pagination'),
    })
}

export const useTemplates = (params: HookParams) => {
  const variables = useMemo(() => paramsToVariables(params), [params])

  const { data: dataTemplates, loading, fetchMore, refetch } = useTemplatesAll(variables)

  const needFetchMore =
    !loading &&
    (dataTemplates?.data.pagination.total || 0) > (dataTemplates?.data.templates.length || 0)

  const fetchMoreTemplates = useCallback(async () => {
    await fetchMore({
      variables: R.mergeDeepRight(variables, {
        data: { pagination: { offset: dataTemplates?.data.templates.length } },
      }),
    })
  }, [fetchMore, variables, dataTemplates?.data.templates.length])

  return {
    loading,
    templates: dataTemplates?.data.templates || EMPTY_ARRAY,
    fetchMore: needFetchMore ? fetchMoreTemplates : undefined,
    refetch,
  }
}

export const useEmployeePreferencesTemplate = ({
  collection,
  preferencesTemplates,
  order,
  tab,
  projectId,
}: {
  collection: ICollectionType | null
  preferencesTemplates?: EmployeePreferencesTemplates | null
  order: SortUiEnum | null
  tab: EmployeePreferencesTab
  projectId?: string
}) => {
  const dispatch = useAppDispatch()
  const updatePreferences = useCallback(
    (params: {
      paramsTab?: EmployeePreferencesTab
      paramsCollection?: ICollectionType
      paramsTag?: ITemplateTag
    }) => {
      if (!projectId) {
        return
      }

      const { paramsTab, paramsCollection, paramsTag } = params
      const { id = null, name = null } =
        paramsCollection !== undefined
          ? paramsCollection || { d: null, name: null }
          : collection || { id: null, name: null }
      const variables = {
        templates: {
          ...preferencesTemplates,
          editorTemplateGroupId: id,
          editorTemplateGroupName: name,
          tab: paramsTab || tab,
          ...(paramsTag && { templatesType: { id: paramsTag.id, name: paramsTag.name } }),
          ...(order && {
            order: {
              field: ENUM_VALUE_MAP[order][0][0],
              value: ENUM_VALUE_MAP[order][0][1],
            } as EditorTemplateOrder,
          }),
        },
      }
      dispatch(setEmployeeToProjectPreferences(variables))
    },
    [projectId, collection, preferencesTemplates, tab, order, dispatch],
  )

  return { updatePreferences }
}

export const getTagsPermission = (
  permissions: PermissionService,
  createdFor: TemplateCollectionCreatedFor,
) => {
  const key = {
    [TemplateCollectionCreatedFor.general]: null,
    [TemplateCollectionCreatedFor.company]: [PermissionsKey.company_collections_company_u],
    [TemplateCollectionCreatedFor.employee]: [PermissionsKey.company_collections_self],
    [TemplateCollectionCreatedFor.project]: [PermissionsKey.project_collection_u],
  }[createdFor]

  return key ? permissions.has(key) : false
}

export interface ITemplatePickerContext {
  companyId: string
  mode: TemplatePickerMode
  onClick: (value: TemplatesListSchemaFragment, e: React.MouseEvent) => void
  projectId: string
  removeByIndex: (value: number, e: React.MouseEvent) => void
  search?: string
  setSearch: React.Dispatch<React.SetStateAction<string | undefined>>
  selected: {
    multi: string[]
    [BlockMode.cover]: string | null
    [BlockMode.start]: string | null
    [BlockMode.end]: string | null
  }
  setCollection: (value: ICollectionType) => void
  setTemplates: React.Dispatch<React.SetStateAction<TemplatesListSchemaFragment[]>>
  step: number | null
  sectionType?: SectionTypeEnum
  sectionId?: string
  setStep: React.Dispatch<React.SetStateAction<number | null>>
  allSelected: string[]
  blocksByMode?: Record<string, boolean>
  tag: string | null
  setTag: (id: string | null) => void
  showTags: boolean
  columns?: number
  setColumns: (value?: number | SetState<number> | undefined) => void
  tab: EmployeePreferencesTab
  order: SortUiEnum | null
  setOrder: React.Dispatch<React.SetStateAction<SortUiEnum>>
  showBrand?: boolean
  collection: {
    id: string
    createdFor?: TemplateCollectionCreatedFor | undefined
    name?: string | undefined
  } | null
  tagOptions: { label: string; value: string | null }[]
  blockMode?: BlockMode[]
}

export const DEFAULT_CONTEXT_VALUE = {
  companyId: '',
  mode: TemplatePickerMode.multi,
  onClick: NOOP,
  projectId: '',
  removeByIndex: NOOP,
  search: '',
  setSearch: NOOP,
  selected: {
    multi: EMPTY_ARRAY,
    [BlockMode.cover]: null,
    [BlockMode.start]: null,
    [BlockMode.end]: null,
  },
  setCollection: NOOP,
  setTemplates: NOOP,
  step: null,
  type: undefined,
  sectionId: '',
  setStep: NOOP,
  allSelected: EMPTY_ARRAY,
  blocksByMode: EMPTY_OBJECT,
  tag: null,
  setTag: NOOP,
  showTags: false,
  columns: 3,
  setColumns: NOOP,
  tab: EmployeePreferencesTab.collections,
  order: null,
  setOrder: NOOP,
  showBrand: false,
  collection: null,
  tagOptions: EMPTY_ARRAY,
}

export const TemplatePickerContext = createContext<ITemplatePickerContext>(DEFAULT_CONTEXT_VALUE)
